import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';
import { MonthCalendarCard, JORNADA_STD } from './Calendario';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];
const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];

function getISOWeekYear(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return { week: Math.ceil((((d - yearStart) / 86400000) + 1) / 7), year: d.getFullYear() };
}

function getWeekDates(year, week) {
  const jan4 = new Date(year, 0, 4);
  const dow  = jan4.getDay() || 7;
  const mon  = new Date(jan4);
  mon.setDate(jan4.getDate() - dow + 1 + (week - 1) * 7);
  return [0,1,2,3,4].map(i => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; });
}

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function fmtTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function fmtHorasVivas(entrada) {
  if (!entrada) return null;
  const ms = Date.now() - new Date(entrada).getTime();
  const h  = Math.floor(ms / 3600000);
  const m  = Math.floor((ms % 3600000) / 60000);
  const s  = Math.floor((ms % 60000) / 1000);
  return `${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`;
}

export default function Home() {
  const { user } = useAuthStore();
  const navigate  = useNavigate();
  const isAdmin   = ROLES_ADMIN.includes(user?.rol);

  // Fichaje
  const [hoy,        setHoy]        = useState(null);
  const [fichandoIn, setFichandoIn] = useState(false);
  const [fichandoOut,setFichandoOut]= useState(false);
  const [, setTick] = useState(0);

  // Planificación personal
  const nowW           = getISOWeekYear();
  const [planWeek, setPlanWeek] = useState(nowW.week);
  const [planYear, setPlanYear] = useState(nowW.year);
  const weekDates      = getWeekDates(planYear, planWeek);
  const today          = new Date();
  const [planEmployees, setPlanEmployees] = useState([]);
  const [planGrid,      setPlanGrid]      = useState({});
  const [planMarcas,    setPlanMarcas]    = useState({ festivos: [], vacaciones: {}, bajas: {} });
  const [dayDetail,     setDayDetail]     = useState(null);
  const planUpdatedAt  = useRef(null);
  const myEmployee     = planEmployees.find(e => String(e.user_id) === String(user?.id));

  function planPrevWeek() {
    if (planWeek === 1) { setPlanWeek(52); setPlanYear(y => y - 1); }
    else setPlanWeek(w => w - 1);
  }
  function planNextWeek() {
    if (planWeek === 52) { setPlanWeek(1); setPlanYear(y => y + 1); }
    else setPlanWeek(w => w + 1);
  }
  const isCurrentWeek = planWeek === nowW.week && planYear === nowW.year;

  // Calendario del equipo (solo admin/jefe/administrativo) — mismo módulo que en /calendario
  const [calAño, setCalAño] = useState(today.getFullYear());
  const [calMes, setCalMes] = useState(today.getMonth());
  const [calFestivos, setCalFestivos] = useState([]);
  const [calVacaciones, setCalVacaciones] = useState([]);
  const [calAllVacaciones, setCalAllVacaciones] = useState([]);
  const [calEventos, setCalEventos] = useState([]);
  const [calCoches, setCalCoches] = useState([]);
  const [calMonthFich, setCalMonthFich] = useState({});
  const [calSelectedDay, setCalSelectedDay] = useState(null);

  function calPrevMes() { if (calMes === 0) { setCalMes(11); setCalAño(a => a - 1); } else setCalMes(m => m - 1); setCalSelectedDay(null); }
  function calNextMes() { if (calMes === 11) { setCalMes(0); setCalAño(a => a + 1); } else setCalMes(m => m + 1); setCalSelectedDay(null); }

  useEffect(() => {
    if (!isAdmin) return;
    api.get(`/api/calendar/festivos?año=${calAño}`).then(r => setCalFestivos(r.data || [])).catch(() => {});
    api.get(`/api/calendar/vacaciones?año=${calAño}`).then(r => setCalVacaciones(r.data || [])).catch(() => {});
    api.get(`/api/calendar/vacaciones/all`).then(r => setCalAllVacaciones(r.data || [])).catch(() => {});
    api.get(`/api/calendar/eventos?año=${calAño}`).then(r => setCalEventos(r.data || [])).catch(() => {});
    api.get('/api/coches').then(r => setCalCoches(r.data || [])).catch(() => {});
  }, [isAdmin, calAño]);

  useEffect(() => {
    if (!isAdmin) return;
    const mesFmt = `${calAño}-${String(calMes + 1).padStart(2, '0')}`;
    api.get(`/api/fichajes/mine/month?mes=${mesFmt}`).then(r => {
      const map = {};
      (r.data || []).forEach(f => { map[f.fecha.slice(0, 10)] = f; });
      setCalMonthFich(map);
    }).catch(() => {});
  }, [isAdmin, calAño, calMes]);

  function calBuildDays() {
    const first = new Date(calAño, calMes, 1), last = new Date(calAño, calMes + 1, 0);
    const startDow = (first.getDay() + 6) % 7;
    const arr = [];
    for (let i = 0; i < startDow; i++) arr.push(null);
    for (let d = 1; d <= last.getDate(); d++) arr.push(new Date(calAño, calMes, d));
    return arr;
  }
  const calDays = calBuildDays();

  const calFestivosByDate = {};
  calFestivos.forEach(f => {
    const d = f.fecha?.split('T')[0]; if (!d) return;
    if (!calFestivosByDate[d]) calFestivosByDate[d] = [];
    calFestivosByDate[d].push(f);
  });

  const calEventosByDate = {};
  calEventos.forEach(ev => {
    if (!ev.fecha_inicio) return;
    const d1 = new Date(ev.fecha_inicio + 'T12:00:00'), d2 = new Date(ev.fecha_fin + 'T12:00:00');
    for (const d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
      const ds = localDateStr(new Date(d));
      if (!calEventosByDate[ds]) calEventosByDate[ds] = [];
      calEventosByDate[ds].push(ev);
    }
  });

  const calCochesByDate = {};
  calCoches.forEach(c => {
    (c.eventos || []).forEach(ev => {
      if (!ev.fecha_inicio) return;
      const d1 = new Date(ev.fecha_inicio + 'T12:00:00');
      const d2 = ev.fecha_fin ? new Date(ev.fecha_fin + 'T12:00:00') : d1;
      for (const d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
        const ds = localDateStr(new Date(d));
        if (!calCochesByDate[ds]) calCochesByDate[ds] = [];
        calCochesByDate[ds].push({ coche: c.nombre, tipo: ev.tipo, descripcion: ev.descripcion });
      }
    });
    if (c.itv_caducidad) {
      const ds = c.itv_caducidad.slice(0, 10);
      if (!calCochesByDate[ds]) calCochesByDate[ds] = [];
      calCochesByDate[ds].push({ coche: c.nombre, tipo: 'itv_caducidad', descripcion: null });
    }
  });

  const calMyVacMap = {};
  calVacaciones.forEach(v => {
    if (!v.fecha_inicio) return;
    const d1 = new Date(v.fecha_inicio), d2 = new Date(v.fecha_fin);
    for (const d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) calMyVacMap[localDateStr(new Date(d))] = v.estado;
  });

  const calAdminVacByDate = {};
  calAllVacaciones.forEach(v => {
    if (!v.fecha_inicio || v.estado === 'rechazada') return;
    const d1 = new Date(v.fecha_inicio), d2 = new Date(v.fecha_fin);
    for (const d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
      const ds = localDateStr(new Date(d));
      if (!calAdminVacByDate[ds]) calAdminVacByDate[ds] = [];
      calAdminVacByDate[ds].push(v);
    }
  });

  const calJornada7hSet = new Set(
    calFestivos.filter(f => f.tipo === 'jornada7h').map(f => f.fecha?.split('T')[0]).filter(Boolean)
  );
  const calJornadaStd = ds => calJornada7hSet.has(ds) ? 7 : JORNADA_STD;

  function planDateStr(dayIdx) {
    const d = weekDates[dayIdx];
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }
  function getPlanMarca(empId, dayIdx) {
    const ds = planDateStr(dayIdx);
    if (planMarcas.festivos.includes(ds)) return 'FESTIVO';
    if (planMarcas.bajas[String(empId)]?.includes(ds)) return 'BAJA';
    if (planMarcas.vacaciones[String(empId)]?.includes(ds)) return 'VACACIONES';
    return null;
  }

  // ── Cargar fichaje hoy ───────────────────────────────────────────────────
  const loadHoy = useCallback(async () => {
    const res = await api.get(`/api/fichajes/hoy?fecha=${localDateStr()}`).catch(() => ({ data: null }));
    setHoy(res.data);
  }, []);

  useEffect(() => { loadHoy(); }, [loadHoy]);

  // Cargar empleados, plan y marcas de la semana (solo técnicos)
  useEffect(() => {
    if (isAdmin) return;
    api.get('/api/planificacion/empleados').then(r => setPlanEmployees(r.data || [])).catch(() => {});
    api.get(`/api/planificacion/data?semana=${planWeek}&año=${planYear}`)
      .then(r => { const row = r.data; setPlanGrid(row?.working_data || row?.data || {}); planUpdatedAt.current = row?.updated_at || null; })
      .catch(() => {});
    api.get(`/api/planificacion/marcas?semana=${planWeek}&año=${planYear}`)
      .then(r => setPlanMarcas({ festivos: r.festivos || [], vacaciones: r.vacaciones || {}, bajas: r.bajas || {} }))
      .catch(() => {});
  }, [isAdmin, planWeek, planYear]);

  // Polling silencioso: actualiza si el admin guardó cambios (solo semana actual)
  useEffect(() => {
    if (isAdmin || !isCurrentWeek) return;
    const id = setInterval(async () => {
      if (document.hidden) return;
      try {
        const r = await api.get(`/api/planificacion/data?semana=${planWeek}&año=${planYear}`);
        const row = r.data;
        if (row?.updated_at && row.updated_at !== planUpdatedAt.current) {
          setPlanGrid(row?.working_data || row?.data || {});
          planUpdatedAt.current = row.updated_at;
        }
      } catch {}
    }, 15000);
    return () => clearInterval(id);
  }, [isAdmin, planWeek, planYear, isCurrentWeek]);

  function getCell(empId, dayIdx) {
    const val = planGrid[String(empId)]?.[String(dayIdx)];
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
  }

  // Timer en vivo mientras jornada abierta
  useEffect(() => {
    if (!hoy?.entrada || hoy?.salida) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [hoy]);

  // ── Fichar ───────────────────────────────────────────────────────────────
  async function getPos() {
    return new Promise(resolve => {
      if (!navigator.geolocation) { resolve({}); return; }
      navigator.geolocation.getCurrentPosition(
        p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve({}),
        { timeout: 8000 }
      );
    });
  }

  async function fichar(tipo) {
    if (tipo === 'entrada') setFichandoIn(true);
    else setFichandoOut(true);
    try {
      const pos = await getPos();
      await api.post(`/api/fichajes/${tipo}`, { ...pos, fecha: localDateStr() });
      await loadHoy();
    } catch (e) { alert(e.message); }
    finally { setFichandoIn(false); setFichandoOut(false); }
  }

  // ── Módulos ── mismo orden que la barra lateral: sueltos → Equipo → Documentación
  const MODULES_ADMIN = [
    { to: '/instalaciones',  icon: '📍', label: 'Instalaciones',  desc: 'Consulta y gestión de instalaciones',       accent: '#f59e0b', bg: 'rgba(245,158,11,.08)',  border: 'rgba(245,158,11,.2)'  },
    { to: '/inventario',     icon: '🏭', label: 'Inventario',     desc: 'Stock de materiales y movimientos',         accent: '#22d3ee', bg: 'rgba(34,211,238,.08)',  border: 'rgba(34,211,238,.2)'  },
    { to: '/coches',         icon: '🚗', label: 'Coches',         desc: 'Km, ITV y eventos de los vehículos',        accent: '#84cc16', bg: 'rgba(132,204,22,.08)',  border: 'rgba(132,204,22,.2)'  },
    { to: '/fichajes',       icon: '⏱️', label: 'Fichajes',       desc: 'Control de entradas y salidas del equipo',  accent: '#2dd4bf', bg: 'rgba(45,212,191,.08)',  border: 'rgba(45,212,191,.2)'  },
    { to: '/calendario',     icon: '📅', label: 'Calendario',     desc: 'Registro mensual de fichajes',              accent: '#06b6d4', bg: 'rgba(6,182,212,.08)',   border: 'rgba(6,182,212,.2)'   },
    { to: '/vacaciones',     icon: '🏖️', label: 'Vacaciones',     desc: 'Solicitudes y resumen del equipo',          accent: '#fb7185', bg: 'rgba(251,113,133,.08)', border: 'rgba(251,113,133,.2)' },
    { to: '/planificacion',  icon: '📆', label: 'Planificación',  desc: 'Cuadrícula semanal del equipo',             accent: '#fb923c', bg: 'rgba(251,146,60,.08)',  border: 'rgba(251,146,60,.2)'  },
    { to: '/empleados',      icon: '👥', label: 'Empleados',      desc: 'Gestión del equipo',                        accent: '#10b981', bg: 'rgba(16,185,129,.08)',  border: 'rgba(16,185,129,.2)'  },
    { to: '/actas',          icon: '📋', label: 'Actas',          desc: 'Actualizaciones y actas de mantenimiento',  accent: '#e879f9', bg: 'rgba(232,121,249,.08)', border: 'rgba(232,121,249,.2)' },
    { to: '/partes',         icon: '📄', label: 'Partes',         desc: 'Partes de trabajo',                         accent: '#64748b', bg: 'rgba(100,116,139,.08)', border: 'rgba(100,116,139,.2)' },
    { to: '/endesa',         icon: '⚡', label: 'ENDESA',         desc: 'Ficha · Coches · PEP · PTW · Prejob · Postjob', accent: '#facc15', bg: 'rgba(250,204,21,.08)', border: 'rgba(250,204,21,.2)' },
    { to: '/admin-docs',     icon: '📤', label: 'Subir Docs',     desc: 'Nóminas e instalaciones semanales',         accent: '#a78bfa', bg: 'rgba(167,139,250,.08)', border: 'rgba(167,139,250,.2)' },
    { to: '/gastos',         icon: '💶', label: 'Nota de Gastos', desc: 'Control de horas y dietas semanales',       accent: '#3b82f6', bg: 'rgba(59,130,246,.08)',  border: 'rgba(59,130,246,.2)'  },
    { to: '/perfil',         icon: '👤', label: 'Mi Perfil',      desc: 'Datos personales',                          accent: '#94a3b8', bg: 'rgba(148,163,184,.08)', border: 'rgba(148,163,184,.2)' },
  ];

  const MODULES_BASE = [
    { to: '/calendario',     icon: '📅', label: 'Calendario',     desc: 'Registro mensual de fichajes',              accent: '#06b6d4', bg: 'rgba(6,182,212,.08)',   border: 'rgba(6,182,212,.2)'  },
    { to: '/instalaciones',  icon: '📍', label: 'Instalaciones',  desc: 'Consulta y gestión de instalaciones',       accent: '#f59e0b', bg: 'rgba(245,158,11,.08)',  border: 'rgba(245,158,11,.2)'  },
    { to: '/actas',          icon: '📋', label: 'Actas',          desc: 'Actualizaciones y actas de mantenimiento',  accent: '#e879f9', bg: 'rgba(232,121,249,.08)', border: 'rgba(232,121,249,.2)' },
    { to: '/prejob-segavan', icon: '📝', label: 'Prejob Segavan', desc: 'Procedimiento diario de trabajo',           accent: '#f43f5e', bg: 'rgba(244,63,94,.08)',   border: 'rgba(244,63,94,.2)'   },
    { to: '/gastos',         icon: '💶', label: 'Nota de Gastos', desc: 'Control de horas y dietas semanales',       accent: '#3b82f6', bg: 'rgba(59,130,246,.08)',  border: 'rgba(59,130,246,.2)'  },
    { to: '/partes',         icon: '📄', label: 'Partes',         desc: 'Partes de trabajo',                         accent: '#64748b', bg: 'rgba(100,116,139,.08)', border: 'rgba(100,116,139,.2)' },
    { to: '/endesa',         icon: '⚡', label: 'ENDESA',         desc: 'Ficha · Coches · PEP · PTW · Prejob · Postjob', accent: '#facc15', bg: 'rgba(250,204,21,.08)', border: 'rgba(250,204,21,.2)' },
    { to: '/perfil',         icon: '👤', label: 'Mi Perfil',      desc: 'Datos personales y nóminas',                accent: '#10b981', bg: 'rgba(16,185,129,.08)',  border: 'rgba(16,185,129,.2)'  },
  ];

  const MODULES = isAdmin ? MODULES_ADMIN : MODULES_BASE;

  const jornada = hoy?.entrada && !hoy?.salida;

  return (
    <div className="sg-page" style={{ padding: 'clamp(14px,4vw,28px)', maxWidth: 900, margin: '0 auto', width: '100%' }}>

      {/* Welcome */}
      <div style={{ marginBottom: 24, animation: 'sg-fade-up .4s var(--ease) both' }}>
        <h1 style={{ fontSize: 28, fontWeight: 900, marginBottom: 4, letterSpacing: -.5 }}>
          {user?.genero === 'f' ? 'Bienvenida' : 'Bienvenido'}, <span className="sg-grad-text">{user?.nombre?.split(' ')[0]}</span>
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--text-dim)' }}>Panel principal · SegAvan Gestión</p>
      </div>

      {user?.rol === 'admin' && <AdminIndicadores />}

      <KmFridayBanner isAdmin={isAdmin} />

      {/* ── Widget fichaje ── */}
      <div
        onClick={() => navigate('/calendario')}
        style={{
          background: 'var(--surface)',
          border: `1.5px solid ${jornada ? 'color-mix(in srgb, var(--ok) 40%, transparent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-lg)', padding: '18px 20px', marginBottom: 20,
          boxShadow: jornada ? '0 0 24px color-mix(in srgb, var(--ok) 8%, transparent)' : 'none',
          cursor: 'pointer',
          transition: 'border-color .2s var(--ease), box-shadow .2s var(--ease), transform .2s var(--ease-spring)',
          animation: 'sg-fade-up .4s var(--ease) .04s both',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>

          {/* Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className={`sg-tile${jornada ? ' sg-pulse' : ''}`} style={{
                '--tile': jornada ? 'var(--ok)' : 'var(--text-dim)',
                width: 44, height: 44, borderRadius: 'var(--radius-md)', fontSize: 20, flexShrink: 0,
              }}>⏱️</div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' }}>Fichaje hoy</div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 1 }}>
                  {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' })}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 20 }}>
              <div>
                <div style={{ fontSize: 9, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase' }}>Entrada</div>
                <div className="sg-nums" style={{ fontSize: 18, fontWeight: 900, color: hoy?.entrada ? 'var(--ok)' : 'var(--text-dim)' }}>{fmtTime(hoy?.entrada)}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase' }}>Salida</div>
                <div className="sg-nums" style={{ fontSize: 18, fontWeight: 900, color: hoy?.salida ? 'var(--bad)' : 'var(--text-dim)' }}>{fmtTime(hoy?.salida)}</div>
              </div>
              {jornada && (
                <div>
                  <div style={{ fontSize: 9, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase' }}>En curso</div>
                  <div className="sg-nums" style={{ fontSize: 18, fontWeight: 900, color: 'var(--amber)' }}>
                    {fmtHorasVivas(hoy.entrada)}
                  </div>
                </div>
              )}
              {hoy?.salida && hoy?.horas_trabajadas && (
                <div>
                  <div style={{ fontSize: 9, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase' }}>Total</div>
                  <div className="sg-nums" style={{ fontSize: 18, fontWeight: 900, color: 'var(--amber)' }}>
                    {Math.floor(hoy.horas_trabajadas)}h {Math.round((hoy.horas_trabajadas % 1) * 60).toString().padStart(2,'0')}m
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Botón */}
          <div onClick={e => e.stopPropagation()}>
            {!hoy?.entrada && (
              <button onClick={() => fichar('entrada')} disabled={fichandoIn} style={{
                background: 'color-mix(in srgb, var(--ok) 14%, transparent)',
                border: '1.5px solid color-mix(in srgb, var(--ok) 45%, transparent)', color: 'var(--ok)',
                padding: '11px 22px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 800, fontSize: 14,
                transition: 'transform .12s var(--ease)',
              }}>
                {fichandoIn ? '📍 Localizando…' : '▶ Fichar Entrada'}
              </button>
            )}
            {hoy?.entrada && !hoy?.salida && (
              <button onClick={() => fichar('salida')} disabled={fichandoOut} style={{
                background: 'color-mix(in srgb, var(--bad) 14%, transparent)',
                border: '1.5px solid color-mix(in srgb, var(--bad) 45%, transparent)', color: 'var(--bad)',
                padding: '11px 22px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 800, fontSize: 14,
                transition: 'transform .12s var(--ease)',
              }}>
                {fichandoOut ? '📍 Localizando…' : '■ Fichar Salida'}
              </button>
            )}
            {hoy?.entrada && hoy?.salida && (
              <span style={{ fontSize: 13, color: 'var(--ok)', fontWeight: 800 }}>✓ Jornada completada</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Calendario (solo admin/jefe/administrativo) ── */}
      {isAdmin && (
        <div style={{ marginBottom: 20, animation: 'sg-fade-up .4s var(--ease) .06s both' }}>
          <MonthCalendarCard
            año={calAño} mes={calMes} prevMes={calPrevMes} nextMes={calNextMes} days={calDays} todayStr={localDateStr()}
            myVacMap={calMyVacMap} festivosByDate={calFestivosByDate} eventosByDate={calEventosByDate}
            cochesByDate={calCochesByDate} adminVacByDate={calAdminVacByDate}
            isAdmin={isAdmin} hoy={hoy} monthFich={calMonthFich} vacaciones={calVacaciones} allVacaciones={calAllVacaciones}
            selectedDay={calSelectedDay} setSelectedDay={setCalSelectedDay} jornadaStd={calJornadaStd}
          />
        </div>
      )}

      {/* ── Mi planificación (solo técnicos) ── */}
      {!isAdmin && myEmployee && (
        <div style={{ marginBottom: 20, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '18px 20px', animation: 'sg-fade-up .4s var(--ease) .08s both' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase' }}>
              Mi planificación
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {!isCurrentWeek && (
                <button
                  onClick={() => { setPlanWeek(nowW.week); setPlanYear(nowW.year); }}
                  style={{ ...BTN_NAV, fontSize: 10, padding: '4px 8px', color: 'var(--amber)', borderColor: 'var(--amber)' }}>
                  Hoy
                </button>
              )}
              <button onClick={planPrevWeek} style={BTN_NAV}>‹</button>
              <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text)', minWidth: 80, textAlign: 'center' }}>
                Sem {planWeek} · {planYear}
              </span>
              <button onClick={planNextWeek} style={BTN_NAV}>›</button>
            </div>
          </div>
          <div className="plan-day-grid" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[0,1,2,3,4].map(dayIdx => {
              const date       = weekDates[dayIdx];
              const isToday    = date.toDateString() === today.toDateString();
              const myVals     = getCell(myEmployee.id, dayIdx);
              const marca      = getPlanMarca(myEmployee.id, dayIdx);
              const companions = myVals.length > 0
                ? planEmployees.filter(e => {
                    if (e.id === myEmployee.id) return false;
                    return getCell(e.id, dayIdx).some(v => myVals.includes(v));
                  })
                : [];
              return (
                <div key={dayIdx}
                  onClick={() => setDayDetail(dayIdx)}
                  style={{
                  flex: '1 1 110px', borderRadius: 'var(--radius-md)', padding: '10px 12px',
                  background: isToday ? 'color-mix(in srgb, var(--amber) 8%, transparent)' : 'var(--surface2)',
                  border: isToday ? '1.5px solid var(--amber)' : '1px solid var(--border)',
                  cursor: 'pointer',
                  transition: 'transform .15s var(--ease)',
                }}>
                  <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', marginBottom: 5,
                    color: isToday ? 'var(--amber)' : 'var(--text-dim)' }}>
                    {DIAS[dayIdx]} {date.getDate()}/{date.getMonth() + 1}
                  </div>
                  {marca && (() => {
                    const mc = marca === 'FESTIVO' ? 'var(--warn)' : marca === 'BAJA' ? '#f87171' : 'var(--info)';
                    const label = marca === 'FESTIVO' ? '🗓 FESTIVO' : marca === 'BAJA' ? '🏥 BAJA' : '🏖 VACACIONES';
                    return (
                      <div style={{ display: 'inline-block', fontSize: 8, fontWeight: 900, padding: '1px 5px',
                          borderRadius: 3, marginBottom: 4, letterSpacing: '.04em',
                          background: `color-mix(in srgb, ${mc} 15%, transparent)`,
                          color: mc,
                          border: `1px solid color-mix(in srgb, ${mc} 40%, transparent)` }}>
                        {label}
                      </div>
                    );
                  })()}
                  <div style={{ marginBottom: companions.length ? 7 : 0 }}>
                    {myVals.length > 0 ? myVals.map((v, i) => (
                      <div key={i} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', lineHeight: 1.35 }}>{v}</div>
                    )) : !marca ? (
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dim)' }}>—</div>
                    ) : null}
                  </div>
                  {companions.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 5 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: 'var(--info)', textTransform: 'uppercase', marginBottom: 3 }}>Con</div>
                      {companions.map(c => (
                        <div key={c.id} style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.5 }}>
                          · {c.nombre}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {dayDetail !== null && myEmployee && (
        <HomeDayModal
          dayIdx={dayDetail}
          date={weekDates[dayDetail]}
          myEmployee={myEmployee}
          employees={planEmployees}
          grid={planGrid}
          marcas={planMarcas}
          onClose={() => setDayDetail(null)}
        />
      )}

      {/* Módulos */}
      <div style={{
        fontSize: 9.5, fontWeight: 700, color: 'var(--text-dim)', letterSpacing: '.14em', textTransform: 'uppercase', marginBottom: 12,
        animation: 'sg-fade-up .4s var(--ease) .12s both',
      }}>
        MENÚ PRINCIPAL
      </div>
      <div className="sg-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
        {MODULES.map(m => (
          <button
            key={m.to}
            onClick={() => navigate(m.to)}
            style={{
              display: 'flex', alignItems: 'center', gap: 16,
              background: 'var(--surface)', border: '1.5px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '18px 20px',
              cursor: 'pointer', textAlign: 'left', width: '100%',
              transition: 'border-color .2s var(--ease), background .2s var(--ease), transform .25s var(--ease-spring), box-shadow .25s var(--ease)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = m.accent;
              e.currentTarget.style.background  = 'var(--surface2)';
              e.currentTarget.style.transform   = 'translateY(-3px)';
              e.currentTarget.style.boxShadow   = `0 14px 32px -12px ${m.accent}55, var(--shadow)`;
              const icon = e.currentTarget.querySelector('.mod-icon');
              if (icon) icon.style.transform = 'scale(1.1) rotate(-4deg)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.background  = 'var(--surface)';
              e.currentTarget.style.transform   = '';
              e.currentTarget.style.boxShadow   = '';
              const icon = e.currentTarget.querySelector('.mod-icon');
              if (icon) icon.style.transform = '';
            }}
          >
            <div className="mod-icon sg-tile--soft" style={{
              '--tile': m.accent,
              width: 48, height: 48, borderRadius: 'var(--radius-md)',
              fontSize: 22, flexShrink: 0,
            }}>{m.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{m.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{m.desc}</div>
            </div>
            <span style={{ fontSize: 18, color: 'var(--text-dim)' }}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const BTN_NAV = { background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text)',
  padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700 };

function toArr(val) {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

function HomeDayModal({ dayIdx, date, myEmployee, employees, grid, marcas, onClose }) {
  const myVals = toArr(grid[String(myEmployee.id)]?.[String(dayIdx)]);
  const ds = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
  let marca = null;
  if (marcas.festivos.includes(ds)) marca = 'FESTIVO';
  else if (marcas.bajas?.[String(myEmployee.id)]?.includes(ds)) marca = 'BAJA';
  else if (marcas.vacaciones[String(myEmployee.id)]?.includes(ds)) marca = 'VACACIONES';

  const myDescs = grid['__descs']?.[String(myEmployee.id)]?.[String(dayIdx)] || {};

  const companions = employees.filter(e => {
    if (e.id === myEmployee.id) return false;
    return toArr(grid[String(e.id)]?.[String(dayIdx)]).some(v => myVals.includes(v));
  });

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 9000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
        padding: 22, maxWidth: 420, width: '100%', boxShadow: 'var(--shadow)',
        animation: 'sg-fade-up .2s var(--ease) both',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 900, color: 'var(--amber)' }}>
              {DIAS[dayIdx]} {date.getDate()}/{date.getMonth()+1}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{myEmployee.nombre}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ marginBottom: companions.length > 0 ? 14 : 0 }}>
          {marca && (() => {
            const mc = marca === 'FESTIVO' ? 'var(--warn)' : marca === 'BAJA' ? '#f87171' : 'var(--info)';
            const label = marca === 'FESTIVO' ? '🗓 FESTIVO' : marca === 'BAJA' ? '🏥 BAJA' : '🏖 VACACIONES';
            return (
              <div style={{ display: 'inline-block', fontSize: 9, fontWeight: 900, padding: '2px 6px',
                  borderRadius: 4, marginBottom: 8, letterSpacing: '.05em',
                  background: `color-mix(in srgb, ${mc} 15%, transparent)`,
                  color: mc,
                  border: `1px solid color-mix(in srgb, ${mc} 40%, transparent)` }}>
                {label}
              </div>
            );
          })()}
          {myVals.length > 0 ? myVals.map((v, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', lineHeight: 1.4 }}>{v}</div>
              {myDescs[v] && (
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4, lineHeight: 1.5 }}>
                  {myDescs[v]}
                </div>
              )}
            </div>
          )) : !marca && (
            <div style={{ fontSize: 13, color: 'var(--text-dim)', padding: '4px 0' }}>Sin trabajo asignado</div>
          )}
        </div>

        {companions.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--info)', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '.06em' }}>
              Compañeros
            </div>
            {companions.map(c => (
              <div key={c.id} style={{ fontSize: 12, color: 'var(--text-dim)', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: 'var(--info)' }}>·</span>{c.nombre}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Panel de indicadores (solo admin) ────────────────────────────────────────

function AdminIndicadores() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/api/dashboard/summary').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const tiles = [
    { label: 'Horas este mes', val: `${data.horas_mes.toLocaleString('es-ES', { maximumFractionDigits: 0 })} h`, accent: '#06b6d4', to: '/calendario' },
    { label: 'Gastos este mes', val: `${data.gastos_mes.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`, accent: '#3b82f6', to: '/gastos' },
    { label: 'Vacaciones pendientes', val: data.vacaciones_pendientes, accent: data.vacaciones_pendientes > 0 ? '#facc15' : '#10b981', to: '/calendario' },
    { label: 'Reembolsos pendientes', val: data.reembolsos_pendientes, accent: data.reembolsos_pendientes > 0 ? '#facc15' : '#10b981', to: '/calendario' },
    { label: 'Vehículos con alerta', val: data.vehiculos_alerta, accent: data.vehiculos_alerta > 0 ? '#f87171' : '#10b981', to: '/coches' },
    { label: 'Empleados activos', val: data.empleados_activos, accent: '#a78bfa', to: '/empleados' },
  ];

  return (
    <div style={{ marginBottom: 20, animation: 'sg-fade-up .4s var(--ease) both' }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--text-dim)', letterSpacing: '.14em', textTransform: 'uppercase', marginBottom: 10 }}>
        Indicadores
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10 }}>
        {tiles.map(t => (
          <button
            key={t.label}
            onClick={() => navigate(t.to)}
            style={{
              textAlign: 'left', cursor: 'pointer', background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '12px 14px', transition: 'border-color .15s var(--ease)',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
          >
            <div className="sg-nums" style={{ fontSize: 22, fontWeight: 900, color: t.accent }}>{t.val}</div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 4 }}>{t.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Aviso viernes: km del coche ──────────────────────────────────────────────

// Viernes más reciente (hoy si es viernes, si no el anterior), como clave
// estable para no repetir el aviso más de una vez por semana aunque se
// abra la app otro día de la ventana (sáb/dom/lun).
function fridayOfThisWeek() {
  const d = new Date();
  const diff = (d.getDay() + 2) % 7; // Vie=0, Sáb=1, Dom=2, Lun=3, Mar=4, Mié=5, Jue=6
  d.setDate(d.getDate() - diff);
  return localDateStr(d);
}
// Ventana del aviso: viernes, sábado, domingo o lunes siguiente —
// por si no abren la app justo el viernes.
function dentroDeVentanaKm() {
  const dow = new Date().getDay();
  return dow === 5 || dow === 6 || dow === 0 || dow === 1;
}

// Los técnicos solo lo ven en la ventana viernes-lunes (y una vez por semana).
// El admin lo ve siempre disponible en Inicio, para poder probarlo o
// rellenar el km de un coche manualmente cualquier día.
function normNombreCoche(s) { return (s || '').trim().toLowerCase(); }
// Clave estable para el <select>: el id real de /api/coches, o "new:<nombre>"
// para vehículos que solo existen como documento en ENDESA (sin fila propia
// todavía, por eso no tienen km base).
function optKey(c) { return c.id != null ? String(c.id) : `new:${c.nombre}`; }

function KmFridayBanner({ isAdmin }) {
  const [visible, setVisible] = useState(false);
  const [coches, setCoches]   = useState([]);
  const [cocheId, setCocheId] = useState('');
  const [km, setKm]           = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone]       = useState(false);
  const [aplicado, setAplicado] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      if (!dentroDeVentanaKm()) return;
      const key = fridayOfThisWeek();
      if (localStorage.getItem(`sgv_km_ok_${key}`) || localStorage.getItem(`sgv_km_skip_${key}`)) return;
    }
    Promise.all([
      api.get('/api/coches').catch(() => ({ data: [] })),
      api.get('/api/endesa/docs').catch(() => ({ data: [] })),
    ]).then(([cRes, eRes]) => {
      const registrados = cRes.data || [];
      const nombresRegistrados = new Set(registrados.map(c => normNombreCoche(c.nombre)));
      const soloEndesa = [];
      const vistos = new Set();
      (eRes.data || []).filter(d => d.seccion === 'coches' && d.subseccion).forEach(d => {
        const key = normNombreCoche(d.subseccion);
        if (nombresRegistrados.has(key) || vistos.has(key)) return;
        vistos.add(key);
        soloEndesa.push({ id: null, nombre: d.subseccion, km_actual: null });
      });
      setCoches([...registrados, ...soloEndesa].sort((a, b) => a.nombre.localeCompare(b.nombre)));
    }).catch(() => setCoches([]));
    setVisible(true);
  }, [isAdmin]);

  async function enviar() {
    if (!cocheId || !km) return;
    setSending(true);
    try {
      let id = cocheId;
      if (id.startsWith('new:')) {
        const r = await api.post('/api/coches', { nombre: id.slice(4) });
        id = String(r.data.id);
      }
      const r = await api.patch(`/api/coches/${id}/km`, { km_actual: km });
      localStorage.setItem(`sgv_km_ok_${fridayOfThisWeek()}`, '1');
      setAplicado(r.data?.aplicado !== false);
      setDone(true);
      setTimeout(() => setVisible(false), 2600);
    } catch (e) { alert('No se pudo enviar: ' + e.message); }
    finally { setSending(false); }
  }

  function ahoraNo() {
    if (!isAdmin) localStorage.setItem(`sgv_km_skip_${fridayOfThisWeek()}`, '1');
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="sg-page" style={{
      background: 'color-mix(in srgb, var(--info) 8%, transparent)',
      border: '1.5px solid color-mix(in srgb, var(--info) 35%, transparent)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', marginBottom: 20,
    }}>
      {done ? (
        <div style={{ fontSize: 13, fontWeight: 700, color: aplicado ? 'var(--ok)' : 'var(--warn)' }}>
          {aplicado ? '✓ Gracias, km registrado.' : '✓ Km recibido, pero ya había uno más alto registrado para ese coche.'}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', marginBottom: 10 }}>
            🚗 ¿Qué coche has usado esta semana? Indica su km actual.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select className="sg-input" value={cocheId} onChange={e => {
              const id = e.target.value;
              setCocheId(id);
              const c = coches.find(c => optKey(c) === id);
              setKm(c?.km_actual != null ? String(c.km_actual) : '');
            }} style={{ width: 'auto', minWidth: 160 }}>
              <option value="">— Elige el coche —</option>
              {coches.map(c => (
                <option key={optKey(c)} value={optKey(c)}>
                  {c.nombre}{c.km_actual != null ? ` (${c.km_actual.toLocaleString('es-ES')} km)` : ' (??? km)'}
                </option>
              ))}
            </select>
            <input className="sg-input" type="number" min="0" placeholder="Km actual" value={km} onChange={e => setKm(e.target.value)}
              style={{ width: 120 }} />
            <button className="sg-btn-primary" onClick={enviar} disabled={sending || !cocheId || !km}>
              {sending ? '…' : 'Enviar'}
            </button>
            <button onClick={ahoraNo} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>
              Ahora no
            </button>
          </div>
          {cocheId && (() => {
            const c = coches.find(c => optKey(c) === cocheId);
            return c?.km_actual != null ? (
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}>
                Último km registrado: {c.km_actual.toLocaleString('es-ES')} — ajusta el valor si hace falta.
              </div>
            ) : null;
          })()}
        </>
      )}
    </div>
  );
}
